// ID-format presets offered when building a dynamic type's regex. Each instance
// of a dynamic type must follow the same format so paths stay predictable.
// Shared by the learn-phase and build-phase create-type forms.
export const ID_PATTERNS = {
  guid: {
    label: "GUID",
    pattern: "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}",
    description:
      "Lowercase hex GUID, e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  },
  slug: {
    label: "Slug",
    pattern: "[a-z0-9]+(-[a-z0-9]+)*",
    description: "Lowercase kebab-case, e.g. my-resource",
  },
  number: {
    label: "Number",
    pattern: "[0-9]+",
    description: "Numeric ID, e.g. 42",
  },
  custom: {
    label: "Custom",
    pattern: "",
    description: "Write your own regex pattern",
  },
} as const;

export type IdPatternKey = keyof typeof ID_PATTERNS;

export function literalValue(nameRegex: string): string {
  return nameRegex.replace(/^\^/, "").replace(/\$$/, "");
}

// ── Dynamic regex builder ─────────────────────────────────────────────────────
// Dynamic types follow the convention `^<literal prefix>(?<placeholder>pattern)$`.
// The named capture group is what GET /api/config uses to derive the path-segment
// template (e.g. `^rel-(?<tenantId>…)$` → `rel-{tenantId}`), so consumers pass raw
// ids and never see the prefix. Keep this in sync with the server-side deriver
// (OpenFga.Catalog/SegmentTemplate.cs).

/** Regex group names: letters, digits, underscore; must not start with a digit. */
export function isValidPlaceholder(name: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name);
}

function escapeRegexLiteral(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Composes `^<prefix>(?<placeholder>pattern)$` with the prefix regex-escaped. */
export function composeDynamicRegex(
  prefix: string,
  placeholder: string,
  pattern: string,
): string {
  return `^${escapeRegexLiteral(prefix)}(?<${placeholder}>${pattern})$`;
}

/**
 * Derives the path-segment template from a nameRegex: literals pass through,
 * `(?<name>…)` becomes `{name}`. Returns null when the regex has metacharacters
 * outside named groups (the server then falls back to a slug-derived placeholder).
 * Mirrors OpenFga.Catalog/SegmentTemplate.TryDerive.
 */
export function deriveSegmentTemplate(
  nameRegex: string,
  displayLabels = false,
): string | null {
  const pattern = nameRegex.replace(/^\^/, "").replace(/\$$/, "");
  let result = "";
  let i = 0;
  while (i < pattern.length) {
    const c = pattern[i];
    if (c === "(") {
      if (!pattern.startsWith("(?<", i)) return null;
      const nameEnd = pattern.indexOf(">", i + 3);
      if (nameEnd < 0) return null;
      const name = pattern.slice(i + 3, nameEnd);
      if (!isValidPlaceholder(name)) return null;
      let depth = 1;
      let j = nameEnd + 1;
      while (j < pattern.length && depth > 0) {
        if (pattern[j] === "\\") j++;
        else if (pattern[j] === "(") depth++;
        else if (pattern[j] === ")") depth--;
        j++;
      }
      if (depth !== 0) return null;
      if (displayLabels) {
        const innerPattern = pattern.slice(nameEnd + 1, j - 1);
        const label = patternLabel(innerPattern);
        result += label ? `{${label}}` : `{${name}}`;
      } else {
        result += `{${name}}`;
      }
      i = j;
    } else if (c === "\\" && i + 1 < pattern.length) {
      const next = pattern[i + 1];
      if (/[A-Za-z0-9]/.test(next)) return null; // \d \w etc. are character classes
      result += next;
      i += 2;
    } else if ("[]{}()*+?|.\\".includes(c)) {
      return null;
    } else {
      result += c;
      i++;
    }
  }
  return result.length > 0 ? result : null;
}

export function extractPlaceholderName(
  nameRegex: string | null | undefined,
): string | null {
  if (!nameRegex) return null;
  const m = nameRegex.match(/\(\?<([A-Za-z_][A-Za-z0-9_]*)>/);
  return m ? m[1] : null;
}

function patternLabel(innerPattern: string): string | null {
  for (const [key, val] of Object.entries(ID_PATTERNS)) {
    if (key !== "custom" && val.pattern === innerPattern) return val.label;
  }
  return null;
}
